import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  UsersIcon, 
  PlusIcon,
  MagnifyingGlassIcon,
  CogIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  EllipsisVerticalIcon,
  LockClosedIcon,
  CalendarIcon,
  MapPinIcon,
  KeyIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import userService, { User, Role } from '../services/users';
import { useToast, ToastContainer } from '../components/common/Toast';
import Dialog from '../components/common/Dialog';
import EditUserModal from '../components/administration/EditUserModal';
import UserActionConfirmModal from '../components/administration/UserActionConfirmModal';
import UserSiteRolesModal from '../components/administration/UserSiteRolesModal';
import ResetPasswordModal from '../components/administration/ResetPasswordModal';
import authService from '../services/auth';
import tenantUserService from '../services/tenantUserService';
import { UserProfile } from '../types/auth';

// Permissions simplifiées pour éviter les problèmes d'import
const PERMISSIONS = {
  USER_MANAGEMENT_VIEW: 'user_management.view',
  USER_MANAGEMENT_CREATE: 'user_management.create',
  USER_MANAGEMENT_EDIT: 'user_management.edit',
  USER_MANAGEMENT_DELETE: 'user_management.delete',
  USER_MANAGEMENT_ASSIGN_ROLES: 'user_management.assign_roles',
};

// Composants de permissions simplifiés pour éviter les erreurs d'API
const SimplePermissionGuard: React.FC<{ permission: string; children: React.ReactNode; fallback?: React.ReactNode }> = ({ children }) => {
  // Pour l'instant, on affiche toujours le contenu (permissions désactivées)
  return <>{children}</>;
};

const SimplePermissionButton: React.FC<{ permission: string; children: React.ReactNode; onClick?: (e?: React.MouseEvent) => void; className?: string; disabled?: boolean; title?: string }> = ({ children, onClick, className, disabled, title }) => {
  return (
    <button onClick={onClick} className={className} disabled={disabled} title={title}>
      {children}
    </button>
  );
};

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { toasts, showSuccess, showError } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showActionsMenu, setShowActionsMenu] = useState<string | null>(null);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSiteRolesModal, setShowSiteRolesModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'block' | 'unblock' | 'force-password-change' | 'delete'>('block');
  const [blockReason, setBlockReason] = useState('');
  const [blockDetails, setBlockDetails] = useState('');
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState<string | null>(null);
  
  // États pour le filtrage par tenant/site
  const [currentUserProfile, setCurrentUserProfile] = useState<UserProfile | null>(null);
  const [currentTenantUser, setCurrentTenantUser] = useState<any>(null);
  const [isTenantUser, setIsTenantUser] = useState(false);
  const [isSiteUser, setIsSiteUser] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  // Charger les rôles depuis le backend
  useEffect(() => {
    loadRoles();
  }, []);

  // Détecter le rôle de l'utilisateur connecté
  useEffect(() => {
    checkUserRole();
  }, []);

  // Charger les utilisateurs depuis le backend
  useEffect(() => {
    loadUsers();
  }, [currentPage, searchTerm, selectedRole, selectedStatus, isTenantUser, isSiteUser, isPlatformAdmin, currentTenantUser, currentUserProfile]);

  // Fermer le menu d'actions en cliquant en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showActionsMenu) {
        const target = event.target as Element;
        const isClickOnMenu = target.closest('[data-menu="actions"]');
        
        if (!isClickOnMenu) {
          setShowActionsMenu(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActionsMenu]);

  const checkUserRole = async () => {
    try {
      const userProfile = await authService.getUserProfile();
    
      setCurrentUserProfile(userProfile);
      
      const userRole = userProfile.role?.name;
      
      const tenantRoles = ['TENANT_ADMIN', 'TENANT_MANAGER', 'TENANT_OWNER', 'TENANT_USER'];
      const siteRoles = ['SITE_ADMIN', 'SITE_MANAGER', 'SITE_SUPERVISOR'];
      const platformRoles = ['PLATFORM_ADMIN', 'PLATFORM_SUPER_ADMIN', 'PLATFORM_SUPPORT'];
      
      if (userRole && tenantRoles.includes(userRole)) {
    
        setIsTenantUser(true);
        setIsSiteUser(false);
        setIsPlatformAdmin(false);
        
        try {
          const tenantUserInfo = await tenantUserService.getCurrentTenantUser();
          if (tenantUserInfo) {
            setCurrentTenantUser(tenantUserInfo);
            
          }
        } catch (error) {
          console.warn('⚠️ Impossible de récupérer les informations tenant user:', error);
        }
      } else if (userRole && siteRoles.includes(userRole)) {
        
        setIsTenantUser(false);
        setIsSiteUser(true);
        setIsPlatformAdmin(false);
        
        
      } else if (userRole && platformRoles.includes(userRole)) {
       
        setIsTenantUser(false);
        setIsSiteUser(false);
        setIsPlatformAdmin(true);
      } else {
        
        setIsTenantUser(false);
        setIsSiteUser(false);
        setIsPlatformAdmin(false);
      }
      
      // Charger les utilisateurs après avoir défini le rôle
     
      loadUsers();
    } catch (error) {
      console.error('❌ Erreur lors de la vérification du rôle utilisateur:', error);
      setIsTenantUser(false);
      setIsSiteUser(false);
      setIsPlatformAdmin(false);
    }
  };

  const loadRoles = async () => {
    try {
      setIsLoadingRoles(true);
      const rolesData = await userService.getRoles();
      // S'assurer que rolesData est un tableau
      if (Array.isArray(rolesData)) {
        setRoles(rolesData);
      } else {
        console.warn('⚠️ Les rôles ne sont pas un tableau:', rolesData);
        setRoles([]);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des rôles:', error);
      showError('Erreur', 'Impossible de charger les rôles depuis le backend');
      setRoles([]);
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Si aucun rôle n'a été détecté, on attend
      if (!isPlatformAdmin && !isTenantUser && !isSiteUser && !currentUserProfile) {
       
        setIsLoading(false);
        return;
      }
      
      const response = await userService.getUsers(currentPage, pageSize, searchTerm);
      if (response && response.results) {
        // Log pour tous les utilisateurs pour voir la structure
        console.log('🔍 Debug - Premiers utilisateurs reçus:', response.results.slice(0, 3).map((u: any) => ({
          username: u.username,
          last_login: u.last_login,
          date_joined: u.date_joined
        })));
        
        let adaptedUsers = response.results.map((user: any) => {
          let displayName = user.full_name;
          if (!displayName || displayName.trim() === '') {
            if (user.first_name && user.last_name) {
              displayName = `${user.first_name} ${user.last_name}`;
            } else if (user.first_name) {
              displayName = user.first_name;
            } else if (user.last_name) {
              displayName = user.last_name;
            } else {
              displayName = user.username.charAt(0).toUpperCase() + user.username.slice(1);
            }
          }
          
          return {
            id: user.id.toString(),
            username: user.username,
            email: user.email || '',
            name: displayName,
            phone: user.phone || '',
            locale: 'fr',
            is_blocked: !user.is_active,
            must_change_password: false,
            is_active: user.is_active,
            created_at: user.date_joined,
            updated_at: user.updated_at || user.date_joined, // Utiliser updated_at si disponible, sinon date_joined
            last_login: user.last_login || null, // Conserver last_login comme champ séparé (peut être null)
            twofa_enabled: false,
            status: (user.is_active ? 'ACTIVE' : 'BLOCKED') as 'ACTIVE' | 'BLOCKED',
            // Ajouter des informations pour le filtrage
            tenant_id: user.tenant_id || null,
            assigned_sites: user.assigned_sites || []
          };
        });

        // Appliquer les filtres selon le rôle de l'utilisateur connecté
        
        
        if (isPlatformAdmin) {
          console.log('👑 Mode admin plateforme: Affichage de tous les utilisateurs');
          // Les admins plateforme voient tous les utilisateurs
        } else if (isTenantUser && currentTenantUser) {
          
          adaptedUsers = adaptedUsers.filter(user => {
            // Filtrer les utilisateurs qui appartiennent au même tenant
            return user.tenant_id === currentTenantUser.tenant_id;
          });
          
        } else if (isSiteUser && currentUserProfile?.sites) {
          
          const assignedSiteIds = currentUserProfile.sites.map((site: any) => site.id);
          adaptedUsers = adaptedUsers.filter(user => {
            // Filtrer les utilisateurs qui ont des sites assignés en commun
            return user.assigned_sites.some((siteId: number) => assignedSiteIds.includes(siteId));
          });
          
        } else {
          console.log('⚠️ Aucun filtre applicable, affichage de tous les utilisateurs (mode par défaut)');
        }
        
        setUsers(adaptedUsers);
        
        // Calculer le nombre total d'utilisateurs et de pages
        const totalCount = response.count || adaptedUsers.length;
        setTotalUsers(totalCount);
        setTotalPages(Math.ceil(totalCount / pageSize));
      } else {
        setUsers([]);
        setTotalUsers(0);
        setTotalPages(0);
      }
    } catch (error: any) {
      console.error('❌ Erreur lors du chargement des utilisateurs:', error);
      setError('Impossible de charger les utilisateurs depuis le backend');
      showError('Erreur', `Impossible de charger les utilisateurs: ${error.message || 'Erreur inconnue'}`);
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setIsLoading(false);
    }
  };





  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = selectedStatus === 'all' || 
                         (selectedStatus === 'active' && !user.is_blocked) ||
                         (selectedStatus === 'inactive' && user.is_blocked);
    
    return matchesSearch && matchesStatus;
  });

  const activeUsers = users.filter(user => !user.is_blocked).length;

  // Fonctions d'action
  const handleBlockUser = async (user: User) => {
    setSelectedUser(user);
    setShowBlockDialog(true);
  };

  const handleConfirmBlock = async () => {
    if (!selectedUser || !blockReason.trim()) {
      return;
    }
    
    try {
      await userService.blockUser(selectedUser.id, blockReason, blockDetails);
      showSuccess('Succès', 'Utilisateur bloqué avec succès');
      setShowBlockDialog(false);
      resetBlockForm();
      setSelectedUser(null);
      loadUsers();
    } catch (error: any) {
      console.error('❌ Erreur lors du blocage:', error);
      showError('Erreur', `Impossible de bloquer l'utilisateur: ${error.message || 'Erreur inconnue'}`);
    }
  };



  const handleCreateUser = () => {
    navigate('/administration/create-user');
  };

  const handleRefresh = () => {
    loadUsers();
    loadRoles();
  };

  // Fonctions de pagination
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Retourner à la première page
  };

  const handleFirstPage = () => handlePageChange(1);
  const handlePreviousPage = () => handlePageChange(currentPage - 1);
  const handleNextPage = () => handlePageChange(currentPage + 1);
  const handleLastPage = () => handlePageChange(totalPages);

  // Réinitialiser la pagination lors des changements de filtres
  const resetPagination = () => {
    setCurrentPage(1);
  };


  // Fonction pour gérer l'édition d'un utilisateur
  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  // Fonction pour sauvegarder les modifications d'un utilisateur
  const handleSaveUser = async (userData: Partial<User>) => {
    if (!selectedUser) return;
    
    try {
      await userService.updateUser(Number(selectedUser.id), userData);
      showSuccess('Succès', 'Utilisateur modifié avec succès');
      await loadUsers();
      setShowEditModal(false);
      setSelectedUser(null);
    } catch (error: any) {
      console.error('❌ Erreur lors de la modification:', error);
      showError('Erreur', `Impossible de modifier l'utilisateur: ${error.message || 'Erreur inconnue'}`);
      throw error;
    }
  };

  // Fonction pour gérer les rôles par site
  const handleSiteRoles = (user: User) => {
    setSelectedUser(user);
    setShowSiteRolesModal(true);
  };

  // Fonction pour réinitialiser le mot de passe
  const handleResetPassword = async (userId: string, password?: string) => {
    try {
      await userService.resetPassword(userId, password);
      showSuccess('Succès', password 
        ? 'Mot de passe défini avec succès' 
        : 'Mot de passe réinitialisé et envoyé par email');
      await loadUsers();
    } catch (error: any) {
      console.error('❌ Erreur lors de la réinitialisation du mot de passe:', error);
      showError('Erreur', `Impossible de réinitialiser le mot de passe: ${error.message || 'Erreur inconnue'}`);
      throw error;
    }
  };

  // Fonction pour gérer les actions critiques
  const handleCriticalAction = async () => {
    if (!selectedUser) return;
    
    try {
      switch (confirmAction) {
        case 'block':
          await userService.blockUser(selectedUser.id, blockReason, blockDetails);
          showSuccess('Succès', 'Utilisateur bloqué avec succès');
          break;
        case 'unblock':
          await userService.unblockUser(selectedUser.id, 'Déblocage manuel');
          showSuccess('Succès', 'Utilisateur débloqué avec succès');
          break;
        case 'force-password-change':
          await userService.forcePasswordChange(selectedUser.id);
          showSuccess('Succès', 'Changement de mot de passe forcé');
          break;
        case 'delete':
          await userService.deleteUser(selectedUser.id);
          showSuccess('Succès', 'Utilisateur supprimé avec succès');
          break;
      }
      
      await loadUsers();
      setShowConfirmModal(false);
      setSelectedUser(null);
      resetBlockForm();
    } catch (error: any) {
      console.error('❌ Erreur lors de l\'action critique:', error);
      showError('Erreur', `Impossible d'effectuer l'action: ${error.message || 'Erreur inconnue'}`);
      throw error;
    }
  };

  // Fonction pour ouvrir le modal de confirmation
  const openConfirmModal = (user: User, action: 'block' | 'unblock' | 'force-password-change' | 'delete') => {
    setSelectedUser(user);
    setConfirmAction(action);
    setShowConfirmModal(true);
  };

  // Fonction pour réinitialiser le formulaire de blocage
  const resetBlockForm = () => {
    setBlockReason('');
    setBlockDetails('');
  };

  return (
    <SimplePermissionGuard permission={PERMISSIONS.USER_MANAGEMENT_VIEW} fallback={<div className="p-6 text-center text-gray-500">Chargement des permissions...</div>}>
      <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Link to="/dashboard" className="p-2 text-gray-400 hover:text-gray-600 mr-4">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-xl font-medium text-dark-black">Gestion des Utilisateurs</h1>
            <p className="text-sm text-gray-600">Administration des comptes et des rôles</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Indicateur de mode de filtrage */}
          {isTenantUser && currentTenantUser && (
            <div className="bg-blue-100 px-3 py-2 rounded-lg">
              <span className="text-sm font-medium text-blue-800">
                Tenant: {currentTenantUser.tenant_name || `ID ${currentTenantUser.tenant_id}`}
                ({currentTenantUser.role})
              </span>
            </div>
          )}
          {isSiteUser && currentUserProfile && (
            <div className="bg-green-100 px-3 py-2 rounded-lg">
              <span className="text-sm font-medium text-green-800">
                Sites assignés: {currentUserProfile.sites?.length || 0} site(s) 
                ({currentUserProfile.role?.name})
              </span>
            </div>
          )}
          
          
          <button 
            onClick={handleRefresh}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2 font-medium shadow-sm hover:shadow transition bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-2 focus:ring-gray-500/40 focus:outline-none disabled:opacity-50"
          >
            <svg className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Actualiser
          </button>
          <SimplePermissionButton
            permission={PERMISSIONS.USER_MANAGEMENT_CREATE}
            onClick={handleCreateUser}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2 font-medium shadow-sm hover:shadow transition bg-bright-orange text-white hover:bg-orange-600 focus:ring-2 focus:ring-orange-500/40 focus:outline-none"
          >
            <PlusIcon className="h-4 w-4" />
            Nouvel utilisateur
          </SimplePermissionButton>
        </div>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <XCircleIcon className="h-5 w-5 text-red-400 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Erreur de connexion au backend</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-sm card-hover">
          <div className="flex items-center mb-2">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
              <UsersIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total utilisateurs</p>
              <p className="text-3xl font-bold text-dark-black">{totalUsers}</p>
            </div>
          </div>
          <p className="text-sm text-blue-600">
            Page {currentPage} sur {totalPages} ({pageSize} par page)
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm card-hover">
          <div className="flex items-center mb-2">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Utilisateurs actifs</p>
              <p className="text-3xl font-bold text-dark-black">{activeUsers}</p>
            </div>
          </div>
          <p className="text-sm text-green-600">
            {totalUsers > 0 ? Math.round((activeUsers / totalUsers) * 100) : 0}% du total
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm card-hover">
          <div className="flex items-center mb-2">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
              <ShieldCheckIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Rôles configurés</p>
              <p className="text-3xl font-bold text-dark-black">{roles.length}</p>
            </div>
          </div>
          <p className="text-sm text-purple-600">
            {isLoadingRoles ? 'Chargement...' : 'Niveaux d\'accès'}
          </p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="form-label">Recherche</label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Nom, username ou email..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  resetPagination();
                }}
                className="form-input pl-10"
              />
            </div>
          </div>
          <div>
            <label className="form-label">Rôle</label>
            <select
              value={selectedRole}
              onChange={(e) => {
                setSelectedRole(e.target.value);
                resetPagination();
              }}
              className="form-select"
              disabled={isLoadingRoles}
            >
              <option value="all">Tous les rôles</option>
              {Array.isArray(roles) && roles.map(role => (
                <option key={role.id} value={role.name || role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Statut</label>
            <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                resetPagination();
              }}
              className="form-select"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedRole('all');
                setSelectedStatus('all');
                resetPagination();
              }}
              className="inline-flex items-center gap-2 rounded-xl px-4 py-2 font-medium shadow-sm hover:shadow transition bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-2 focus:ring-gray-500/40 focus:outline-none w-full"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-medium text-dark-black">Liste des utilisateurs</h3>
          {!isLoading && (
            <p className="text-sm text-gray-500 mt-1">
              {filteredUsers.length} utilisateur(s) sur cette page • {totalUsers} total • Page {currentPage} sur {totalPages}
            </p>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom complet
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom d'utilisateur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dernière connexion
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                      <span className="ml-3 text-gray-600">Chargement des utilisateurs...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    {error ? 'Erreur de connexion au backend' : 'Aucun utilisateur trouvé'}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-bright-orange rounded-full flex items-center justify-center mr-3">
                          <span className="text-white font-medium text-sm">
                            {user.name.split(' ').map((n: string) => n[0]).join('')}
                          </span>
                        </div>
                        <div className="text-sm font-medium text-dark-black">{user.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">@{user.username}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.email ? (
                        <div className="flex items-center text-sm text-gray-600">
                          <EnvelopeIcon className="h-4 w-4 mr-2" />
                          {user.email}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                   
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        !user.is_blocked 
                          ? 'bg-green-100 text-green-800 border border-green-200' 
                          : 'bg-red-100 text-red-800 border border-red-200'
                      }`}>
                        {!user.is_blocked ? (
                          <>
                            <CheckCircleIcon className="h-3 w-3 mr-1" />
                            Actif
                          </>
                        ) : (
                          <>
                            <XCircleIcon className="h-3 w-3 mr-1" />
                            Bloqué
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(() => {
                        // Utiliser last_login en priorité (c'est le champ spécifique pour la dernière connexion)
                        const loginDate = user.last_login;
                        
                        if (loginDate) {
                          try {
                            const date = new Date(loginDate);
                            // Vérifier que la date est valide
                            if (!isNaN(date.getTime())) {
                              return date.toLocaleString('fr-FR', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              });
                            } else {
                              console.warn('⚠️ Date invalide pour', user.username, ':', loginDate);
                            }
                          } catch (e) {
                            console.warn('❌ Erreur lors du formatage de la date pour', user.username, ':', e, loginDate);
                          }
                        }
                        return 'Jamais connecté';
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => navigate(`/administration/user/${user.id}`)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-blue-600 hover:text-blue-900 hover:bg-blue-50 transition-colors focus:ring-2 focus:ring-blue-500/40 focus:outline-none" 
                          title="Voir les détails"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <SimplePermissionButton
                          permission={PERMISSIONS.USER_MANAGEMENT_EDIT}
                          onClick={() => handleEditUser(user)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-green-600 hover:text-green-900 hover:bg-green-50 transition-colors focus:ring-2 focus:ring-green-500/40 focus:outline-none" 
                          title="Modifier"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </SimplePermissionButton>
                        
                        {/* Menu d'actions avancées */}
                        <div className="relative">
                          <button 
                            onClick={() => {
                              if (showActionsMenu === user.id) {
                                setShowActionsMenu(null);
                              } else {
                                setShowActionsMenu(user.id);
                                setSelectedUser(user);
                              }
                            }}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors focus:ring-2 focus:ring-gray-500/40 focus:outline-none" 
                            title="Plus d'actions"
                          >
                            <EllipsisVerticalIcon className="h-4 w-4" />
                          </button>
                          
                          {showActionsMenu === user.id && (
                            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-10" data-menu="actions">
                              <div className="py-1">
                                {!user.is_blocked ? (
                                  <>
                                    {/* Options pour les utilisateurs actifs */}
                                    <SimplePermissionButton
                                      permission={PERMISSIONS.USER_MANAGEMENT_EDIT}
                                      onClick={(e) => {
                                        e?.stopPropagation();
                                        setShowActionsMenu(null);
                                        handleBlockUser(user);
                                      }}
                                      className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                                    >
                                      <LockClosedIcon className="h-4 w-4 mr-2" />
                                      Bloquer l'utilisateur
                                    </SimplePermissionButton>
                                    
                                    <SimplePermissionButton
                                      permission={PERMISSIONS.USER_MANAGEMENT_ASSIGN_ROLES}
                                      onClick={(e) => {
                                        e?.stopPropagation();
                                        setShowActionsMenu(null);
                                        handleSiteRoles(user);
                                      }}
                                      className="flex items-center w-full px-4 py-2 text-sm text-blue-700 hover:bg-blue-50"
                                    >
                                      <MapPinIcon className="h-4 w-4 mr-2" />
                                      Gérer les assignations
                                    </SimplePermissionButton>
                                    
                                    <button
                                      type='button'
                                      onClick={async (e) => {
                                        e?.stopPropagation();
                                        setShowActionsMenu(null);
                                        
                                        try {
                                          // Récupérer les détails complets de l'utilisateur
                                          const userDetails = await userService.getUserById(user.id);
                                          navigate(`/administration/user/${user.id}/sessions`, {
                                            state: {
                                              user: userDetails,
                                              siteAssignments: userDetails.site_roles || []
                                            }
                                          });
                                        } catch (error) {
                                          console.error('❌ Erreur lors de la récupération des détails utilisateur:', error);
                                          // Navigation de fallback avec les données disponibles
                                          navigate(`/administration/user/${user.id}/sessions`, {
                                            state: {
                                              user: user,
                                              siteAssignments: []
                                            }
                                          });
                                        }
                                      }}
                                      className="flex items-center w-full px-4 py-2 text-sm text-purple-700 hover:bg-purple-50"
                                    >
                                      <CalendarIcon className="h-4 w-4 mr-2" />
                                      Gérer les sessions
                                    </button>
                                    
                                    <SimplePermissionButton
                                      permission={PERMISSIONS.USER_MANAGEMENT_EDIT}
                                      onClick={(e) => {
                                        e?.stopPropagation();
                                        setShowActionsMenu(null);
                                        openConfirmModal(user, 'force-password-change');
                                      }}
                                      className="flex items-center w-full px-4 py-2 text-sm text-orange-700 hover:bg-orange-50"
                                    >
                                      <ClockIcon className="h-4 w-4 mr-2" />
                                      Forcer changement MDP
                                    </SimplePermissionButton>

                                    <SimplePermissionButton
                                      permission={PERMISSIONS.USER_MANAGEMENT_EDIT}
                                      onClick={(e) => {
                                        e?.stopPropagation();
                                        setShowActionsMenu(null);
                                        setShowResetPasswordModal(true);
                                      }}
                                      className="flex items-center w-full px-4 py-2 text-sm text-cyan-700 hover:bg-cyan-50"
                                    >
                                      <KeyIcon className="h-4 w-4 mr-2" />
                                      Réinitialiser mot de passe
                                    </SimplePermissionButton>
                                  </>
                                ) : (
                                  <>
                                    {/* Options pour les utilisateurs bloqués */}
                                    <SimplePermissionButton
                                      permission={PERMISSIONS.USER_MANAGEMENT_EDIT}
                                      onClick={(e) => {
                                        e?.stopPropagation();
                                        openConfirmModal(user, 'unblock');
                                      }}
                                      className="flex items-center w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                                    >
                                      <KeyIcon className="h-4 w-4 mr-2" />
                                      Débloquer l'utilisateur
                                    </SimplePermissionButton>
                                  </>
                                )}

                                {/* Options communes (toujours visibles) */}
                                <SimplePermissionButton
                                  permission={PERMISSIONS.USER_MANAGEMENT_DELETE}
                                  onClick={(e) => {
                                    e?.stopPropagation();
                                    setShowActionsMenu(null);
                                    openConfirmModal(user, 'delete');
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-red-800 hover:bg-red-50 border-t border-red-100 pt-2 mt-2"
                                >
                                  <TrashIcon className="h-4 w-4 mr-2" />
                                  Supprimer l'utilisateur
                                </SimplePermissionButton>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            {/* Informations de pagination */}
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-700">
                Affichage de <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> à{' '}
                <span className="font-medium">{Math.min(currentPage * pageSize, totalUsers)}</span> sur{' '}
                <span className="font-medium">{totalUsers}</span> utilisateurs
              </div>
              
              {/* Sélecteur de taille de page */}
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-700">Afficher:</label>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="rounded-lg border border-gray-300 px-3 py-1 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-700">par page</span>
              </div>
            </div>

            {/* Contrôles de navigation */}
            <div className="flex items-center space-x-2">
              {/* Première page */}
              <button
                onClick={handleFirstPage}
                disabled={currentPage === 1}
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                title="Première page"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>

              {/* Page précédente */}
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                title="Page précédente"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Numéros de pages */}
              <div className="flex items-center space-x-1">
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                  
                  // Ajuster si on est proche de la fin
                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  // Page 1 si pas visible
                  if (startPage > 1) {
                    pages.push(
                      <button
                        key={1}
                        onClick={() => handlePageChange(1)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(
                        <span key="ellipsis1" className="px-2 text-gray-400">
                          ...
                        </span>
                      );
                    }
                  }

                  // Pages visibles
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => handlePageChange(i)}
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                          i === currentPage
                            ? 'bg-bright-orange text-white'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  // Dernière page si pas visible
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <span key="ellipsis2" className="px-2 text-gray-400">
                          ...
                        </span>
                      );
                    }
                    pages.push(
                      <button
                        key={totalPages}
                        onClick={() => handlePageChange(totalPages)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                      >
                        {totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
              </div>

              {/* Page suivante */}
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                title="Page suivante"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Dernière page */}
              <button
                onClick={handleLastPage}
                disabled={currentPage === totalPages}
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                title="Dernière page"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-6 flex justify-between items-center">
        <div className="flex space-x-3">
          <Link 
            to="/permissions"
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2 font-medium shadow-sm hover:shadow transition bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-2 focus:ring-gray-500/40 focus:outline-none"
          >
            <CogIcon className="h-4 w-4" />
            Gérer les rôles
          </Link>
          <Link 
            to="/permissions"
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2 font-medium shadow-sm hover:shadow transition bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-2 focus:ring-gray-500/40 focus:outline-none"
          >
            <ShieldCheckIcon className="h-4 w-4" />
            Permissions
          </Link>
        </div>
        <div className="text-sm text-gray-500">
          {filteredUsers.length} utilisateur(s) sur cette page • {totalUsers} total
        </div>
      </div>

      {/* Dialogue de blocage d'utilisateur */}
      <Dialog
        isOpen={showBlockDialog}
        onClose={() => setShowBlockDialog(false)}
        title="Bloquer l'utilisateur"
        size="md"
      >
        <div className="space-y-6">
          {/* Avertissement */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Action critique</h3>
                <p className="text-sm text-red-700 mt-1">
                  L'utilisateur <strong>{selectedUser?.name}</strong> ne pourra plus se connecter à l'application.
                </p>
              </div>
            </div>
          </div>
          
          {/* Motif principal */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motif du blocage <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              <input
                list="blockReasons"
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="Sélectionnez ou saisissez un motif..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
              />
              <datalist id="blockReasons">
                <option value="Tentative d'accès non autorisé" />
                <option value="Violation de la politique de sécurité" />
                <option value="Utilisation d'identifiants compromis" />
                <option value="Tentative de connexion suspecte" />
                <option value="Utilisation abusive du système" />
                <option value="Violation des règles d'utilisation" />
                <option value="Comportement non professionnel" />
                <option value="Harcèlement ou intimidation" />
                <option value="Non-respect des procédures internes" />
                <option value="Violation des règles de confidentialité" />
                <option value="Non-conformité aux normes de sécurité" />
                <option value="Manquement aux obligations légales" />
                <option value="Compte temporaire expiré" />
                <option value="Changement de poste ou de service" />
                <option value="Suspension administrative" />
                <option value="Réorganisation des accès" />
                <option value="Investigation en cours" />
                <option value="Formation de sécurité requise" />
                <option value="Audit des accès" />
                <option value="Maintenance du compte" />
              </datalist>
              <p className="text-xs text-gray-500">
                💡 Vous pouvez sélectionner un motif prédéfini ou saisir votre propre justification.
              </p>
            </div>
          </div>

          {/* Détails supplémentaires */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Détails supplémentaires <span className="text-gray-400">(optionnel)</span>
            </label>
            <textarea
              placeholder="Ajoutez des détails, des informations contextuelles ou des instructions pour le déblocage..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              rows={3}
              value={blockDetails}
              onChange={(e) => setBlockDetails(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-1">
              📝 Exemples : dates, incidents spécifiques, actions requises, contacts...
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                setShowBlockDialog(false);
                resetBlockForm();
              }}
              className="flex-1 rounded-xl px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleConfirmBlock}
              disabled={!blockReason.trim()}
              className="flex-1 rounded-xl px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              {!blockReason.trim() ? (
                <>
                  <LockClosedIcon className="h-4 w-4 mr-2" />
                  Motif requis
                </>
              ) : (
                <>
                  <LockClosedIcon className="h-4 w-4 mr-2" />
                  Bloquer l'utilisateur
                </>
              )}
            </button>
          </div>
        </div>
      </Dialog>


      {/* Modal d'édition d'utilisateur */}
      <EditUserModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedUser(null);
        }}
        onSave={handleSaveUser}
        user={selectedUser}
        roles={roles}
        loading={isLoading}
      />

      {/* Modal de confirmation pour les actions critiques */}
      <UserActionConfirmModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setSelectedUser(null);
          setBlockReason('');
          setBlockDetails('');
        }}
        onConfirm={handleCriticalAction}
        user={selectedUser}
        action={confirmAction}
        loading={isLoading}
      />

      {/* Modal pour les rôles par site */}
      <UserSiteRolesModal
        isOpen={showSiteRolesModal}
        onClose={() => {
          setShowSiteRolesModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
      />

      {/* Modal de réinitialisation de mot de passe */}
      <ResetPasswordModal
        isOpen={showResetPasswordModal}
        onClose={() => {
          setShowResetPasswordModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onResetPassword={handleResetPassword}
        loading={isLoading}
      />

      {/* Container des toasts */}
      <ToastContainer toasts={toasts} position="top-right" />
      </div>
    </SimplePermissionGuard>
  );
};

export default UsersPage;
